import React from 'react';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { APP_SELECT_TRIGGER_CLASS } from '../../../ui/controlStyles';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { leadSelectDropdownFields, leadSelectPrimaryLabel, type LeadSelectFields } from '../../../utils/leadSelectOption';
import type { MergeProjectResult } from '../utils/mergeProjectSummary';

type MergeLead = LeadSelectFields & { id: string | number; contact_id?: string | number | null };

type MergeProjectModalProps = {
  open: boolean;
  onClose: () => void;
  source: MergeLead | null;
  onMerged: (result: MergeProjectResult) => void;
};

/** „In anderes Projekt überführen“: Zielprojekt wählen, alles umhängen, Quelle in den Papierkorb. */
export function MergeProjectModal({ open, onClose, source, onMerged }: MergeProjectModalProps) {
  const [leads, setLeads] = React.useState<MergeLead[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [targetId, setTargetId] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setTargetId('');
    setError(null);
    let cancelled = false;
    setLoading(true);
    fetch('/api/leads', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: unknown) => {
        if (cancelled) return;
        setLeads(Array.isArray(data) ? (data as MergeLead[]).filter((l) => l?.id) : []);
      })
      .catch(() => {
        if (!cancelled) setLeads([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const options = React.useMemo(
    () =>
      leads
        .filter((lead) => String(lead.id) !== String(source?.id ?? ''))
        .map((lead) => ({ value: String(lead.id), ...leadSelectDropdownFields(lead) })),
    [leads, source?.id],
  );

  const target = leads.find((lead) => String(lead.id) === targetId) ?? null;
  const differentCustomer =
    Boolean(target) && String(source?.contact_id ?? '') !== String(target?.contact_id ?? '');

  const handleConfirm = async () => {
    if (!source || !targetId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(String(source.id))}/merge-into`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_id: targetId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError((body as { error?: string }).error ?? 'Projekt konnte nicht überführt werden.');
        return;
      }
      onMerged(body as MergeProjectResult);
    } catch {
      setError('Projekt konnte nicht überführt werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const sourceLabel = source ? leadSelectPrimaryLabel(source) : '';

  return (
    <AppConfirmDialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title="In anderes Projekt überführen"
      confirmLabel="Überführen"
      destructive
      isLoading={submitting}
      confirmDisabled={!targetId || loading}
      onConfirm={handleConfirm}
      description={
        <div className="space-y-3">
          <p>
            Alle Inhalte von <span className="font-medium text-app-text">{sourceLabel}</span> – Vorgänge,
            Termine, Dokumente, Rechnungen, Ist-Zeiten, Aufgaben, E-Mails und Notizen – werden in das Zielprojekt
            übertragen. Das leere Projekt kommt danach in den Papierkorb.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-app-text-muted">Zielprojekt</label>
            <AppSelectDropdown
              className="w-full"
              value={targetId}
              onChange={setTargetId}
              placeholder={loading ? 'Projekte werden geladen …' : 'Projekt wählen …'}
              searchable
              searchPlaceholder="Projekt suchen …"
              disabled={loading || submitting}
              triggerClassName={APP_SELECT_TRIGGER_CLASS}
              menuZIndex={UI_Z_INDEX.portalDropdownInModal}
              options={options}
            />
          </div>
          {differentCustomer ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Das Zielprojekt gehört zu einem anderen Kunden. Rechnungen behalten den Kunden, an den sie gestellt wurden.
            </p>
          ) : null}
          <p className="text-xs text-app-text-subtle">
            Das Überführen kann nicht automatisch rückgängig gemacht werden.
          </p>
          {error ? <p className="text-sm text-red-700">{error}</p> : null}
        </div>
      }
    />
  );
}
